'use client';

import React, { useState, useEffect } from 'react';
import { X, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { t } from '@/locales';
import { useBoatTypes } from '@/hooks/useBoatTypes';
import CatalogLocationCascade from './CatalogLocationCascade';

export type CatalogSort = 'recommended' | 'price-asc' | 'price-desc' | 'capacity' | 'length' | 'year-new' | 'rating';

export interface CatalogFiltersState {
  boatTypes: string[];
  priceMin: number | null;
  priceMax: number | null;
  capacityMin: number;
  capacityMax: number;
  /** Filtro città (denominato 'port' per compatibilità storica). Es. "Olbia". */
  port: string | null;
  /** Zona geografica. Es. "Costa Smeralda". */
  zone: string | null;
  /** Porto specifico: FK pois (category=marina). */
  port_id: string | null;
  /** Etichetta display per il chip del porto selezionato. */
  where_label: string | null;
  noLicenseOnly: boolean;
  skipperAvailable: boolean;
  // Quando true e l'utente ha selezionato un range date, il backend
  // esclude le barche con CLOSED / blocchi / bookings overlap.
  onlyAvailableInRange: boolean;
  // Filtri estesi (allineamento Boataround/SamBoat)
  lengthMin?: number | null;
  lengthMax?: number | null;
  cabinsMin?: number;
  bathroomsMin?: number;
  yearMin?: number | null;
  crewedOnly?: boolean;
  instantBookingOnly?: boolean;
  hostessAvailable?: boolean;
  freeCancellation?: boolean;
  petFriendly?: boolean;
  hasPromo?: boolean;
  ratingMin?: number;
  manufacturers?: string[];
  amenities?: string[];
  sort?: CatalogSort;
}

// Indica se il toggle "solo disponibili" e' applicabile (cioe' utente ha range date).
interface AvailabilityContext {
  hasDateRange: boolean;
}

interface CatalogFiltersProps {
  filters: CatalogFiltersState;
  onFiltersChange: (filters: CatalogFiltersState) => void;
  availableCities?: string[];  // Preferred - list of cities
  availablePorts?: string[];   // Legacy - fallback to ports
  /** Brands/produttori distinti presenti nella fleet corrente. Render multi-checkbox. */
  availableManufacturers?: string[];
  /** Amenity catalog distinto. key è il filtro DB, label è display. */
  availableAmenities?: Array<{ key: string; label: string }>;
  priceRange: { min: number; max: number };
  isOpen?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
  // Se true il checkbox "solo disponibili" e' attivabile; se false viene
  // mostrato disabilitato con hint "seleziona prima le date".
  availability?: AvailabilityContext;
}

// Boat types ora arrivano dinamicamente da DB via useBoatTypes hook
// (mantenuto stub per backward-compat di altri import).

const CAPACITY_OPTIONS = Array.from({ length: 19 }, (_, i) => i + 2);

const CatalogFilters: React.FC<CatalogFiltersProps> = ({
  filters,
  onFiltersChange,
  availableCities = [],
  availablePorts = [],
  availableManufacturers = [],
  availableAmenities = [],
  priceRange,
  isOpen = true,
  onClose,
  isMobile = false,
  availability = { hasDateRange: false },
}) => {
  const { options: boatTypeOptions } = useBoatTypes();

  // Filtri condizionati al tipo barca: alcune sezioni non hanno senso per jetski/
  // dinghy (no cabine/bagni/anno-specific). showXxx vero quando selezione vuota
  // (= tutti i tipi) oppure include almeno un tipo dove il filtro ha senso.
  const sel = filters.boatTypes;
  const isOnly = (slugs: string[]) => sel.length > 0 && sel.every((s) => slugs.includes(s));
  const onlyJetski = isOnly(['jetski', 'moto-acqua', 'jet-ski']);
  const onlyDinghy = isOnly(['dinghy', 'gommone']);
  const noCabinTypes = onlyJetski || onlyDinghy;
  const showCabins = !noCabinTypes;
  const showBathrooms = !noCabinTypes;
  const showCrewedOnly = !onlyJetski; // jetski non ha skipper
  const showHostess = !onlyJetski && !onlyDinghy;
  const showLicense = !onlyJetski; // jetski richiede sempre patente o specifico

  const [expandedSections, setExpandedSections] = useState({
    boatType: true,
    price: true,
    capacity: true,
    port: true,
    options: true,
    length: false,
    cabins: false,
    bathrooms: false,
    year: false,
    rating: false,
    amenities: false,
    brand: false,
  });

  const [localPriceMin, setLocalPriceMin] = useState<string>(filters.priceMin?.toString() || '');
  const [localPriceMax, setLocalPriceMax] = useState<string>(filters.priceMax?.toString() || '');

  useEffect(() => {
    setLocalPriceMin(filters.priceMin?.toString() || '');
    setLocalPriceMax(filters.priceMax?.toString() || '');
  }, [filters.priceMin, filters.priceMax]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleBoatTypeToggle = (type: string) => {
    const newTypes = filters.boatTypes.includes(type)
      ? filters.boatTypes.filter(t => t !== type)
      : [...filters.boatTypes, type];
    onFiltersChange({ ...filters, boatTypes: newTypes });
  };

  const handlePriceChange = () => {
    const min = localPriceMin ? parseInt(localPriceMin) : null;
    const max = localPriceMax ? parseInt(localPriceMax) : null;
    onFiltersChange({ ...filters, priceMin: min, priceMax: max });
  };

  const handleReset = () => {
    onFiltersChange({
      boatTypes: [],
      priceMin: null,
      priceMax: null,
      capacityMin: 2,
      capacityMax: 20,
      port: null,
      zone: null,
      port_id: null,
      where_label: null,
      noLicenseOnly: false,
      skipperAvailable: false,
      onlyAvailableInRange: true,
      lengthMin: null,
      lengthMax: null,
      cabinsMin: 0,
      bathroomsMin: 0,
      yearMin: null,
      crewedOnly: false,
      hostessAvailable: false,
      instantBookingOnly: false,
      freeCancellation: false,
      petFriendly: false,
      hasPromo: false,
      ratingMin: 0,
      manufacturers: [],
      amenities: [],
    });
    setLocalPriceMin('');
    setLocalPriceMax('');
  };

  const activeFiltersCount = [
    filters.boatTypes.length > 0,
    filters.priceMin !== null || filters.priceMax !== null,
    filters.capacityMin > 2 || filters.capacityMax < 20,
    filters.port !== null || filters.zone !== null || filters.port_id !== null,
    filters.noLicenseOnly,
    filters.skipperAvailable,
    !!filters.crewedOnly,
    !!filters.hostessAvailable,
    !!filters.instantBookingOnly,
    !!filters.freeCancellation,
    !!filters.petFriendly,
    !!filters.hasPromo,
    filters.lengthMin != null || filters.lengthMax != null,
    (filters.cabinsMin ?? 0) > 0,
    (filters.bathroomsMin ?? 0) > 0,
    (filters.ratingMin ?? 0) > 0,
    filters.yearMin != null,
    (filters.manufacturers?.length ?? 0) > 0,
    (filters.amenities?.length ?? 0) > 0,
    // "Solo disponibili" conta come filtro attivo solo se sta effettivamente
    // restringendo (cioe' c'e' un range date e il toggle e' on).
    availability.hasDateRange && filters.onlyAvailableInRange,
  ].filter(Boolean).length;

  const FilterSection = ({ title, sectionKey, children }: { title: string; sectionKey: keyof typeof expandedSections; children: React.ReactNode }) => (
    <div className="border-b border-slate-100 last:border-0" data-cid={`catalog-filter-section-${sectionKey}`}>
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="font-semibold text-slate-800">{title}</span>
        {expandedSections[sectionKey] ? (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {expandedSections[sectionKey] && (
        <div className="pb-4" data-cid={`catalog-filter-body-${sectionKey}`}>{children}</div>
      )}
    </div>
  );

  const content = (
    <div className="space-y-0" data-cid="catalog-filters-content">
      {/* Boat Types */}
      <FilterSection title={t('catalog.boatType')} sectionKey="boatType">
        <div className="space-y-2">
          {boatTypeOptions.map(({ value, label }) => (
            <label
              key={value}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={filters.boatTypes.includes(value)}
                onChange={() => handleBoatTypeToggle(value)}
                className="w-5 h-5 rounded-md border-2 border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                {label}
              </span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Price Range */}
      <FilterSection title={t('catalog.dailyPrice')} sectionKey="price">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
              <input
                type="number"
                placeholder={priceRange.min.toString()}
                value={localPriceMin}
                onChange={(e) => setLocalPriceMin(e.target.value)}
                onBlur={handlePriceChange}
                className="w-full pl-8 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
            </div>
            <span className="text-slate-400">—</span>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
              <input
                type="number"
                placeholder={priceRange.max.toString()}
                value={localPriceMax}
                onChange={(e) => setLocalPriceMax(e.target.value)}
                onBlur={handlePriceChange}
                className="w-full pl-8 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
            </div>
          </div>
          <p className="text-xs text-slate-400">
            {t('catalog.priceRange')}: €{priceRange.min} - €{priceRange.max}
          </p>
        </div>
      </FilterSection>

      {/* Capacity */}
      <FilterSection title={t('catalog.passengerCapacity')} sectionKey="capacity">
        <div className="flex items-center gap-2">
          <select
            value={filters.capacityMin}
            onChange={(e) => onFiltersChange({ ...filters, capacityMin: parseInt(e.target.value) })}
            className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          >
            {CAPACITY_OPTIONS.filter(c => c <= filters.capacityMax).map(c => (
              <option key={c} value={c}>Min {c}</option>
            ))}
          </select>
          <span className="text-slate-400">—</span>
          <select
            value={filters.capacityMax}
            onChange={(e) => onFiltersChange({ ...filters, capacityMax: parseInt(e.target.value) })}
            className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          >
            {CAPACITY_OPTIONS.filter(c => c >= filters.capacityMin).map(c => (
              <option key={c} value={c}>Max {c}</option>
            ))}
          </select>
        </div>
      </FilterSection>

      {/* Luogo — cascata Zona ▸ Città ▸ Porto */}
      <FilterSection title="Luogo di partenza" sectionKey="port">
        <CatalogLocationCascade
          zone={filters.zone}
          city={filters.port}
          port_id={filters.port_id}
          onZoneChange={(z) =>
            onFiltersChange({ ...filters, zone: z, port: null, port_id: null, where_label: null })
          }
          onCityChange={(c) =>
            onFiltersChange({ ...filters, port: c, port_id: null, where_label: null })
          }
          onPortChange={(pid, label) =>
            onFiltersChange({ ...filters, port_id: pid, where_label: label })
          }
        />
      </FilterSection>

      {/* Options */}
      <FilterSection title={t('catalog.options')} sectionKey="options">
        <div className="space-y-3">
          {/* Solo barche disponibili nel range date selezionato */}
          <label
            className={`flex items-center gap-3 group ${availability.hasDateRange ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            title={availability.hasDateRange ? '' : 'Seleziona un range di date dalla barra di ricerca'}
          >
            <input
              type="checkbox"
              checked={filters.onlyAvailableInRange}
              disabled={!availability.hasDateRange}
              onChange={() => onFiltersChange({ ...filters, onlyAvailableInRange: !filters.onlyAvailableInRange })}
              className="w-5 h-5 rounded-md border-2 border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            />
            <div className="flex-1 min-w-0">
              <div className={`text-sm transition-colors ${availability.hasDateRange ? 'text-slate-600 group-hover:text-slate-800' : 'text-slate-400'}`}>
                Solo disponibili nelle date scelte
              </div>
              {!availability.hasDateRange && (
                <div className="text-[11px] text-slate-400 mt-0.5">Imposta un range nella ricerca</div>
              )}
            </div>
          </label>

          {showLicense && (
            <label className="flex items-center justify-between cursor-pointer group">
              <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                {t('catalog.noLicense')}
              </span>
              <button
                onClick={() => onFiltersChange({ ...filters, noLicenseOnly: !filters.noLicenseOnly })}
                className={`relative w-11 h-6 rounded-full transition-colors ${filters.noLicenseOnly ? 'bg-blue-600' : 'bg-slate-200'}`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${filters.noLicenseOnly ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </label>
          )}

          {showCrewedOnly && (
            <label className="flex items-center justify-between cursor-pointer group">
              <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                {t('catalog.skipperAvailable')}
              </span>
              <button
                onClick={() => onFiltersChange({ ...filters, skipperAvailable: !filters.skipperAvailable })}
                className={`relative w-11 h-6 rounded-full transition-colors ${filters.skipperAvailable ? 'bg-blue-600' : 'bg-slate-200'}`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${filters.skipperAvailable ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </label>
          )}

          {showCrewedOnly && (
            <label className="flex items-center justify-between cursor-pointer group">
              <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">Solo skipperate (Crewed)</span>
              <button
                onClick={() => onFiltersChange({ ...filters, crewedOnly: !filters.crewedOnly })}
                className={`relative w-11 h-6 rounded-full transition-colors ${filters.crewedOnly ? 'bg-blue-600' : 'bg-slate-200'}`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${filters.crewedOnly ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </label>
          )}

          {showHostess && (
            <label className="flex items-center justify-between cursor-pointer group">
              <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">Hostess disponibile</span>
              <button
                onClick={() => onFiltersChange({ ...filters, hostessAvailable: !filters.hostessAvailable })}
                className={`relative w-11 h-6 rounded-full transition-colors ${filters.hostessAvailable ? 'bg-blue-600' : 'bg-slate-200'}`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${filters.hostessAvailable ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </label>
          )}

          <label className="flex items-center justify-between cursor-pointer group">
            <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">Cancellazione gratuita</span>
            <button
              onClick={() => onFiltersChange({ ...filters, freeCancellation: !filters.freeCancellation })}
              className={`relative w-11 h-6 rounded-full transition-colors ${filters.freeCancellation ? 'bg-blue-600' : 'bg-slate-200'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${filters.freeCancellation ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </label>

          <label className="flex items-center justify-between cursor-pointer group">
            <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">Animali ammessi</span>
            <button
              onClick={() => onFiltersChange({ ...filters, petFriendly: !filters.petFriendly })}
              className={`relative w-11 h-6 rounded-full transition-colors ${filters.petFriendly ? 'bg-blue-600' : 'bg-slate-200'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${filters.petFriendly ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </label>

          <label className="flex items-center justify-between cursor-pointer group">
            <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">In offerta</span>
            <button
              onClick={() => onFiltersChange({ ...filters, hasPromo: !filters.hasPromo })}
              className={`relative w-11 h-6 rounded-full transition-colors ${filters.hasPromo ? 'bg-rose-500' : 'bg-slate-200'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${filters.hasPromo ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </label>

          <label className="flex items-center justify-between cursor-pointer group">
            <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">Conferma istantanea</span>
            <button
              onClick={() => onFiltersChange({ ...filters, instantBookingOnly: !filters.instantBookingOnly })}
              className={`relative w-11 h-6 rounded-full transition-colors ${filters.instantBookingOnly ? 'bg-blue-600' : 'bg-slate-200'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${filters.instantBookingOnly ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </label>
        </div>
      </FilterSection>

      {/* Lunghezza barca */}
      <FilterSection title="Lunghezza (m)" sectionKey="length">
        <div className="flex items-center gap-2">
          <input
            type="number" min={0} max={50} step={1}
            placeholder="Min"
            value={filters.lengthMin ?? ''}
            onChange={(e) => onFiltersChange({ ...filters, lengthMin: e.target.value ? Number(e.target.value) : null })}
            className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm numeric"
          />
          <span className="text-slate-400">—</span>
          <input
            type="number" min={0} max={80} step={1}
            placeholder="Max"
            value={filters.lengthMax ?? ''}
            onChange={(e) => onFiltersChange({ ...filters, lengthMax: e.target.value ? Number(e.target.value) : null })}
            className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm numeric"
          />
        </div>
      </FilterSection>

      {/* Cabine */}
      {showCabins && <FilterSection title="Cabine (min)" sectionKey="cabins">
        <div className="flex items-center gap-2">
          {[0, 1, 2, 3, 4, 5, 6].map((n) => {
            const active = (filters.cabinsMin ?? 0) === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onFiltersChange({ ...filters, cabinsMin: n })}
                className={`w-9 h-9 rounded-lg border-2 text-sm font-bold ${active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
              >
                {n === 0 ? '–' : n}{n === 6 ? '+' : ''}
              </button>
            );
          })}
        </div>
      </FilterSection>}

      {/* Bagni */}
      {showBathrooms && <FilterSection title="Bagni (min)" sectionKey="bathrooms">
        <div className="flex items-center gap-2">
          {[0, 1, 2, 3, 4].map((n) => {
            const active = (filters.bathroomsMin ?? 0) === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onFiltersChange({ ...filters, bathroomsMin: n })}
                className={`w-9 h-9 rounded-lg border-2 text-sm font-bold ${active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
              >
                {n === 0 ? '–' : n}{n === 4 ? '+' : ''}
              </button>
            );
          })}
        </div>
      </FilterSection>}

      {/* Rating minimo */}
      <FilterSection title="Rating minimo" sectionKey="rating">
        <div className="flex items-center gap-2">
          {[0, 3, 3.5, 4, 4.5].map((n) => {
            const active = (filters.ratingMin ?? 0) === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onFiltersChange({ ...filters, ratingMin: n })}
                className={`px-2.5 py-1 rounded-lg border-2 text-xs font-bold ${active ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
              >
                {n === 0 ? 'Tutti' : `${n}★+`}
              </button>
            );
          })}
        </div>
      </FilterSection>

      {/* Anno costruzione */}
      <FilterSection title="Anno costruzione (min)" sectionKey="year">
        <input
          type="number" min={1980} max={new Date().getFullYear()} step={1}
          placeholder="Es. 2015"
          value={filters.yearMin ?? ''}
          onChange={(e) => onFiltersChange({ ...filters, yearMin: e.target.value ? Number(e.target.value) : null })}
          className="w-32 px-3 py-1.5 border border-slate-200 rounded-lg text-sm numeric"
        />
      </FilterSection>

      {/* Brand / Produttore — toggle multi-select */}
      {availableManufacturers.length > 0 && (
        <FilterSection title="Marca" sectionKey="brand">
          <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-1">
            {availableManufacturers.map((m) => {
              const checked = (filters.manufacturers ?? []).includes(m);
              return (
                <label key={m} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 hover:text-slate-900">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const cur = new Set(filters.manufacturers ?? []);
                      if (cur.has(m)) cur.delete(m); else cur.add(m);
                      onFiltersChange({ ...filters, manufacturers: Array.from(cur) });
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>{m}</span>
                </label>
              );
            })}
          </div>
        </FilterSection>
      )}

      {/* Servizi a bordo — amenity multi-select */}
      {availableAmenities.length > 0 && (
        <FilterSection title="Servizi a bordo" sectionKey="amenities">
          <div className="flex flex-col gap-1.5 max-h-72 overflow-y-auto pr-1">
            {availableAmenities.map((a) => {
              const checked = (filters.amenities ?? []).includes(a.key);
              return (
                <label key={a.key} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 hover:text-slate-900">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const cur = new Set(filters.amenities ?? []);
                      if (cur.has(a.key)) cur.delete(a.key); else cur.add(a.key);
                      onFiltersChange({ ...filters, amenities: Array.from(cur) });
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>{a.label}</span>
                </label>
              );
            })}
          </div>
        </FilterSection>
      )}

      {/* Reset Button */}
      <div className="pt-4" data-cid="catalog-filters-reset-wrap">
        <button
          onClick={handleReset}
          disabled={activeFiltersCount === 0}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold text-sm hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <RotateCcw className="w-4 h-4" />
          {t('catalog.resetFilters')}
          {activeFiltersCount > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );

  // Mobile modal version
  if (isMobile) {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-[70] lg:hidden" data-cid="catalog-filters-mobile-modal">
        <div className="absolute inset-0 bg-slate-950/60" onClick={onClose} data-cid="catalog-filters-mobile-backdrop" />
        <div className="absolute inset-y-0 left-0 w-[90%] max-w-sm bg-white shadow-2xl overflow-y-auto flex flex-col" data-cid="catalog-filters-mobile-panel">
          <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between" data-cid="catalog-filters-mobile-header">
            <h2 className="font-display text-lg font-bold text-slate-950 tracking-tight">{t('catalog.filters')}</h2>
            <button
              onClick={onClose}
              aria-label="Chiudi filtri"
              className="p-2 -mr-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-slate-700" />
            </button>
          </div>
          <div className="flex-1 px-5 py-4" data-cid="catalog-filters-mobile-body">
            {content}
          </div>
          <div className="sticky bottom-0 z-10 bg-white border-t border-slate-200 px-5 py-4 shadow-[0_-8px_20px_-8px_rgba(15,23,42,0.1)]" data-cid="catalog-filters-mobile-footer">
            <button
              onClick={onClose}
              className="w-full py-3.5 bg-slate-950 text-white rounded-xl font-semibold text-sm hover:bg-slate-800 transition-colors active:translate-y-px"
            >
              {t('catalog.showResults')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop sidebar version
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.18),0_8px_20px_-8px_rgba(15,23,42,0.1)]" data-cid="catalog-filters-desktop">
      <h2 className="text-lg font-bold text-slate-800 mb-4 pb-4 border-b border-slate-100">
        {t('catalog.filters')}
      </h2>
      {content}
    </div>
  );
};

export default CatalogFilters;
